// Executive Protection Details — list view.
//
// A filterable table of protective details (by status / principal / org) with
// a create + edit form modal and soft-delete. Selecting a row navigates into
// the DetailPlannerPage (timeline, roster, advances, itinerary, motorcade,
// threats). Status changes are driven from the planner via the state machine,
// not edited inline here.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass. The planner route is expected at /details/:id.

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ProtectiveDetail,
  DetailStatus,
  ThreatLevel,
  Principal,
  Organization,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Select,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { ApiError } from '../api/client';
import {
  useDetails,
  useCreateDetail,
  useUpdateDetail,
  useDeleteDetail,
  usePrincipalsLookup,
  useOrganizationsLookup,
  type DetailFilters,
} from '../api/queries/details';

// ---------- Static option lists ----------

export const DETAIL_STATUSES: DetailStatus[] = [
  'planning',
  'advance',
  'active',
  'standby',
  'completed',
  'canceled',
];

export const THREAT_LEVELS: ThreatLevel[] = [
  'low',
  'guarded',
  'elevated',
  'high',
  'severe',
];

// ---------- Shared helpers ----------

export function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function fmtDateTime(epochMs: number | null): string {
  return epochMs !== null ? new Date(epochMs).toLocaleString() : '—';
}

export function fmtDate(epochMs: number | null): string {
  return epochMs !== null ? new Date(epochMs).toLocaleDateString() : '—';
}

export function statusTone(status: DetailStatus): string {
  switch (status) {
    case 'active':
      return 'badge-success';
    case 'advance':
    case 'standby':
      return 'badge-warn';
    case 'canceled':
      return 'badge-danger';
    case 'completed':
      return 'badge-muted';
    default:
      return 'badge';
  }
}

export function threatTone(level: ThreatLevel): string {
  switch (level) {
    case 'severe':
    case 'high':
      return 'badge-danger';
    case 'elevated':
      return 'badge-warn';
    case 'guarded':
      return 'badge';
    default:
      return 'badge-muted';
  }
}

export function StatusBadge({ status }: { status: DetailStatus }): JSX.Element {
  return <span className={`badge ${statusTone(status)}`}>{status}</span>;
}

export function ThreatBadge({ level }: { level: ThreatLevel }): JSX.Element {
  return <span className={`badge ${threatTone(level)}`}>{level}</span>;
}

// Build an id -> name index over a principals list for cheap display lookups.
export function principalName(
  principals: Principal[],
  id: number | null,
): string {
  if (id === null) return '—';
  const p = principals.find((x) => x.id === id);
  return p ? (p.alias ? `${p.full_name} (${p.alias})` : p.full_name) : `#${id}`;
}

export function organizationName(
  orgs: Organization[],
  id: number | null,
): string {
  if (id === null) return '—';
  const o = orgs.find((x) => x.id === id);
  return o ? o.name : `#${id}`;
}

// =============================================================================
// Page
// =============================================================================

export function DetailsPage(): JSX.Element {
  const navigate = useNavigate();
  const canRead = can('detail.read');
  const canWrite = can('detail.write');

  // Radix Select forbids an empty value, so filters use the literal sentinel
  // 'all' for "no filter" and that case is dropped when building DetailFilters.
  const [statusFilter, setStatusFilter] = useState<DetailStatus | 'all'>('all');
  const [principalFilter, setPrincipalFilter] = useState<string>('all');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ProtectiveDetail | null>(null);
  const [deleting, setDeleting] = useState<ProtectiveDetail | null>(null);

  const filters: DetailFilters = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(principalFilter !== 'all' ? { principal_id: Number(principalFilter) } : {}),
      ...(orgFilter !== 'all' ? { organization_id: Number(orgFilter) } : {}),
    }),
    [statusFilter, principalFilter, orgFilter],
  );

  const detailsQuery = useDetails(filters);
  const principalsLookup = usePrincipalsLookup();
  const orgsLookup = useOrganizationsLookup();
  const deleteDetail = useDeleteDetail();

  const details = detailsQuery.data?.details ?? [];
  const principals = principalsLookup.data?.principals ?? [];
  const organizations = orgsLookup.data?.organizations ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...DETAIL_STATUSES.map((s) => ({ value: s, label: s })),
  ];
  const principalFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All principals' },
    ...principals.map((p) => ({ value: String(p.id), label: p.full_name })),
  ];
  const orgFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All organizations' },
    ...organizations.map((o) => ({ value: String(o.id), label: o.name })),
  ];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteDetail.mutateAsync(deleting.id);
      toast.success('Detail deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete detail.'));
    }
  };

  if (!canRead) {
    return (
      <div className="details-page">
        <p className="error-text" role="alert">
          You don’t have permission to view protective details.
        </p>
      </div>
    );
  }

  return (
    <div className="details-page">
      <header className="page-header">
        <div>
          <h2>Protective Details</h2>
          <p className="page-sub">
            Executive protection assignments — planning through close-out.
          </p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New detail
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as DetailStatus | 'all')}
          options={statusFilterOptions}
          aria-label="Filter by status"
        />
        <Select
          value={principalFilter}
          onValueChange={setPrincipalFilter}
          options={principalFilterOptions}
          aria-label="Filter by principal"
        />
        <Select
          value={orgFilter}
          onValueChange={setOrgFilter}
          options={orgFilterOptions}
          aria-label="Filter by organization"
        />
      </div>

      {detailsQuery.isLoading && <p className="muted">Loading details…</p>}
      {detailsQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(detailsQuery.error, 'Failed to load details.')}
        </p>
      )}
      {!detailsQuery.isLoading && details.length === 0 && (
        <p className="muted empty-state">No protective details match these filters.</p>
      )}

      {details.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Principal</Table.HeaderCell>
              <Table.HeaderCell>Organization</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Threat</Table.HeaderCell>
              <Table.HeaderCell>Start</Table.HeaderCell>
              <Table.HeaderCell>End</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {details.map((d) => (
              <Table.Row
                key={d.id}
                interactive
                onClick={() => navigate(`/details/${d.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/details/${d.id}`);
                }}
              >
                <Table.Cell tone="strong">
                  {principalName(principals, d.principal_id)}
                </Table.Cell>
                <Table.Cell tone="muted">
                  {organizationName(organizations, d.organization_id)}
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge status={d.status} />
                </Table.Cell>
                <Table.Cell>
                  <ThreatBadge level={d.threat_level} />
                </Table.Cell>
                <Table.Cell>{fmtDateTime(d.start_at)}</Table.Cell>
                <Table.Cell>{fmtDateTime(d.end_at)}</Table.Cell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" onClick={() => navigate(`/details/${d.id}`)}>
                    Open
                  </Button>
                  {canWrite && (
                    <>
                      <Button variant="ghost" onClick={() => setEditing(d)}>
                        Edit
                      </Button>
                      <Button variant="danger" onClick={() => setDeleting(d)}>
                        Delete
                      </Button>
                    </>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <DetailFormModal
          mode="create"
          principals={principals}
          organizations={organizations}
          onClose={() => setCreateOpen(false)}
          onSaved={(d) => {
            setCreateOpen(false);
            navigate(`/details/${d.id}`);
          }}
        />
      )}

      {editing && (
        <DetailFormModal
          mode="edit"
          detail={editing}
          principals={principals}
          organizations={organizations}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete protective detail"
        message={
          deleting
            ? `Delete this detail for ${principalName(principals, deleting.principal_id)}? This soft-deletes the detail and its planning records.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteDetail.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Create / edit modal
// =============================================================================

interface DetailFormModalProps {
  mode: 'create' | 'edit';
  detail?: ProtectiveDetail;
  principals: Principal[];
  organizations: Organization[];
  onClose: () => void;
  onSaved: (d: ProtectiveDetail) => void;
}

export function DetailFormModal({
  mode,
  detail,
  principals,
  organizations,
  onClose,
  onSaved,
}: DetailFormModalProps): JSX.Element {
  const create = useCreateDetail();
  const update = useUpdateDetail();
  const busy = create.isPending || update.isPending;

  const [principalId, setPrincipalId] = useState<number | ''>(
    detail?.principal_id ?? '',
  );
  const [organizationId, setOrganizationId] = useState<number | ''>(
    detail?.organization_id ?? '',
  );
  const [threatLevel, setThreatLevel] = useState<ThreatLevel>(
    detail?.threat_level ?? 'low',
  );
  // status is only settable at creation; afterwards it moves via the state
  // machine on the planner page.
  const [status, setStatus] = useState<DetailStatus>(detail?.status ?? 'planning');
  const [startAt, setStartAt] = useState<number | null>(detail?.start_at ?? null);
  const [endAt, setEndAt] = useState<number | null>(detail?.end_at ?? null);
  const [notes, setNotes] = useState(detail?.notes ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (principalId === '') {
      toast.warn('A principal is required.');
      return;
    }
    if (startAt !== null && endAt !== null && endAt < startAt) {
      toast.warn('End must be after start.');
      return;
    }
    const trimmedNotes = notes.trim();
    try {
      if (mode === 'create') {
        const res = await create.mutateAsync({
          principal_id: principalId,
          organization_id: organizationId === '' ? null : organizationId,
          status,
          threat_level: threatLevel,
          start_at: startAt,
          end_at: endAt,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        });
        toast.success('Detail created.');
        onSaved(res.detail);
      } else if (detail) {
        const res = await update.mutateAsync({
          id: detail.id,
          principal_id: principalId,
          organization_id: organizationId === '' ? null : organizationId,
          threat_level: threatLevel,
          start_at: startAt,
          end_at: endAt,
          notes: trimmedNotes ? trimmedNotes : null,
        });
        toast.success('Detail updated.');
        onSaved(res.detail);
      }
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save detail.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New protective detail' : 'Edit detail'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="detail-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="detail-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Principal" required>
          <select
            className="form-input"
            value={principalId}
            onChange={(e) => setPrincipalId(e.target.value ? Number(e.target.value) : '')}
            required
            autoFocus
          >
            <option value="">Select a principal…</option>
            {principals.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
                {p.alias ? ` (${p.alias})` : ''}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Organization">
          <select
            className="form-input"
            value={organizationId}
            onChange={(e) =>
              setOrganizationId(e.target.value ? Number(e.target.value) : '')
            }
          >
            <option value="">None</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>

        {mode === 'create' && (
          <FormField label="Initial status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as DetailStatus)}
            >
              {DETAIL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
        )}

        <FormField label="Threat level">
          <select
            className="form-input"
            value={threatLevel}
            onChange={(e) => setThreatLevel(e.target.value as ThreatLevel)}
          >
            {THREAT_LEVELS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Starts">
          <DateInput value={startAt} onChange={setStartAt} withTime />
        </FormField>

        <FormField label="Ends">
          <DateInput value={endAt} onChange={setEndAt} withTime minEpoch={startAt ?? undefined} />
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={20_000}
            placeholder="Detail brief, special instructions (optional)."
          />
        </FormField>
      </form>
    </Dialog>
  );
}
